/* eslint-disable no-unused-vars */
/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Radio, useMantineColorScheme } from '@mantine/core';
import { DateRangePicker } from '@mantine/dates';

export default function ReusableDateRangePicker({
  title = 'Select date range',
  placeholder = 'Pick a date range',
  value,
  setValue,
  required = false,
  disabled = false,
  error,
}) {
  // Initialize from value prop or empty state
  const initialRadioValue = value?.radioValue || '';
  const initialDateRange = value?.dateRange || [null, null];

  const [internalValue, setInternalValue] = useState(initialDateRange);
  const [radioValue, setRadioValue] = useState(initialRadioValue);
  const [shouldOpenPicker, setShouldOpenPicker] = useState(false);

  const dateInputRef = useRef(null);
  const { colorScheme } = useMantineColorScheme();

  const isValidDateRange = (range) => range && range[0] !== null && range[1] !== null;

  // Helper function to check if we should store the state
  const shouldStoreState = (dateRange, radioVal) => {
    if (!radioVal) return false;

    if (radioVal === 'custom') {
      return isValidDateRange(dateRange);
    }

    // For preset options, always store
    return ['runningQuarter', 'lastQuarter', 'runningQuarterLastMonth', 'lastQuarterLastMonth', 'currentMonth', 'lastMonth', 'monthBeforeLastMonth'].includes(radioVal);
  };

  const getQuarterRange = (type) => {
    const now = dayjs();
    const quarter = Math.floor(now.month() / 3);
    let start; let end;

    if (type === 'runningQuarter') {
      const startMonth = quarter * 3;
      start = dayjs(new Date(now.year(), startMonth, 1));
      end = now;
    }

    if (type === 'lastQuarter') {
      const lastQuarter = quarter === 0 ? 3 : quarter - 1;
      const year = quarter === 0 ? now.year() - 1 : now.year();
      const startMonth = lastQuarter * 3;
      start = dayjs(new Date(year, startMonth, 1));
      end = start.add(2, 'month').endOf('month');
    }

    if (type === 'currentMonth') {
      start = now.startOf('month');
      end = now;
    }

    if (type === 'lastMonth') {
      const lastMonth = now.subtract(1, 'month');
      start = lastMonth.startOf('month');
      end = lastMonth.endOf('month');
    }

    if (type === 'monthBeforeLastMonth') {
      const monthBeforeLast = now.subtract(2, 'month');
      start = monthBeforeLast.startOf('month');
      end = monthBeforeLast.endOf('month');
    }

    return [start.toDate(), end.toDate()];
  };

  const isSameDate = (date1, date2) => dayjs(date1).format('YYYY-MM-DD') === dayjs(date2).format('YYYY-MM-DD');

  const determineRadioOption = (dateRange) => {
    if (!isValidDateRange(dateRange)) return '';

    const latestQuarterRange = getQuarterRange('runningQuarter');
    const lastQuarterRange = getQuarterRange('lastQuarter');
    const currentMonthRange = getQuarterRange('currentMonth');
    const lastMonthRange = getQuarterRange('lastMonth');
    const monthBeforeLastRange = getQuarterRange('monthBeforeLastMonth');

    const isLatestQuarter = isSameDate(dateRange[0], latestQuarterRange[0])
      && isSameDate(dateRange[1], latestQuarterRange[1]);

    const isLastQuarter = isSameDate(dateRange[0], lastQuarterRange[0])
      && isSameDate(dateRange[1], lastQuarterRange[1]);

    const isCurrentMonth = isSameDate(dateRange[0], currentMonthRange[0])
      && isSameDate(dateRange[1], currentMonthRange[1]);

    const isLastMonth = isSameDate(dateRange[0], lastMonthRange[0])
      && isSameDate(dateRange[1], lastMonthRange[1]);

    const isMonthBeforeLast = isSameDate(dateRange[0], monthBeforeLastRange[0])
      && isSameDate(dateRange[1], monthBeforeLastRange[1]);

    if (isLatestQuarter) return 'runningQuarter';
    if (isLastQuarter) return 'lastQuarter';
    if (isCurrentMonth) return 'currentMonth';
    if (isLastMonth) return 'lastMonth';
    if (isMonthBeforeLast) return 'monthBeforeLastMonth';
    return 'custom';
  };

  const handleRadioChange = (val) => {
    setRadioValue(val);

    if (val === 'runningQuarter' || val === 'lastQuarter' || val === 'runningQuarterLastMonth' || val === 'lastQuarterLastMonth' || val === 'currentMonth' || val === 'lastMonth' || val === 'monthBeforeLastMonth') {
      const range = getQuarterRange(val);
      setInternalValue(range);

      setValue({
        dateRange: range,
        radioValue: val,
      });
    }

    if (val === 'custom') {
      setInternalValue([null, null]);

      setValue(null);

      // Open the date picker
      setTimeout(() => {
        dateInputRef.current?.click();
      }, 0);
      setShouldOpenPicker(true);
    }
  };

  const handleChange = (range) => {
    setInternalValue(range);
    const [start, end] = range || [null, null];

    const isCleared = start === null && end === null;

    if (isCleared) {
      // When cleared, reset everything and don't store state
      setRadioValue('');
      // Don't call setValue here - let parent handle undefined/null state
      if (setValue) {
        setValue(null); // or undefined, depending on your parent component's expectation
      }
    } else if (start !== null && end !== null) {
      // Valid range selected
      const newRadioValue = determineRadioOption(range) || 'custom';
      setRadioValue(newRadioValue);

      // Store state only when we have a valid range
      if (shouldStoreState(range, newRadioValue)) {
        setValue({
          dateRange: range,
          radioValue: newRadioValue,
        });
      }
    } else {
      // Partial selection (only start or end date) - don't store state yet
      setRadioValue('custom');
    }
  };

  useEffect(() => {
    if (shouldOpenPicker && radioValue === 'custom') {
      setTimeout(() => {
        setShouldOpenPicker(false);
      }, 0);
    }
  }, [shouldOpenPicker, radioValue]);

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        // Handle legacy array format
        setInternalValue(value);
        const matchedOption = determineRadioOption(value);
        setRadioValue(matchedOption);
      } else if (value.dateRange && value.radioValue) {
        // Handle object format
        setInternalValue(value.dateRange);
        setRadioValue(value.radioValue);
      } else {
        // Handle partial object or invalid value
        setInternalValue([null, null]);
        setRadioValue('');
      }
    } else {
      // Handle null/undefined value
      setInternalValue([null, null]);
      setRadioValue('');
    }
  }, [value]);

  // Handle radio deselection
  const handleRadioDeselect = () => {
    setRadioValue('');
    setInternalValue([null, null]);
    if (setValue) {
      setValue(null);
    }
  };

  return (
    <div className="flex flex-col">
      {title && <div className="font-bold">{title}</div>}
      <Radio.Group
        name="customDateRange"
        value={radioValue}
        onChange={handleRadioChange}
        spacing="xs"
        size="xs"
        sx={{
          '& input:checked': {
            backgroundColor: '#33B1FF',
            borderColor: '#33B1FF',
          },
          '& label': {
            color: colorScheme === 'dark' ? '#BEB9B9' : '#000',
            fontWeight: 500,
            paddingLeft: 8,
          },
        }}
      >
        <Radio value="runningQuarter" label="Running Quarter" />
        <Radio value="lastQuarter" label="Last Quarter" />
        <Radio value="custom" label="Custom Range" />
        <Radio value="currentMonth" label="Current Month" />
        <Radio value="lastMonth" label="Last Month" />
        <Radio value="monthBeforeLastMonth" label="Month Before Last" />
        {/* <Radio value="runningQuarterLastMonth" label="Running
        Quarter Last Month" disabled={isRunningQuarterLastMonthDisabled} />
        <Radio value="lastQuarterLastMonth" label="Last Quarter Last Month" /> */}
      </Radio.Group>

      <DateRangePicker
        placeholder={placeholder}
        value={internalValue}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        error={error}
        clearable
        ref={dateInputRef}
        sx={{
          '& input::placeholder': {
            color: '#898989',
          },
        }}
        styles={{
          input: {
            minHeight: 45,
            height: 45,
            backgroundColor: colorScheme === 'dark' && '#1E1E1E !important',
            border: colorScheme === 'dark' && '1px solid #737373 !important',
          },
          dropdown: {
            backgroundColor: colorScheme === 'dark' ? '#25262B' : '#fff',
            border: colorScheme === 'dark' && '1px solid #737373 !important',
          },
          day: {
            color: colorScheme === 'dark' && '#C1C2C5',
            '&[data-outside]': {
              color: colorScheme === 'dark' && '#5C5F66',
            },
            '&[data-in-range]': {
              backgroundColor: colorScheme === 'dark' && '#1971c233',
            },
            '&[data-selected]': {
              backgroundColor: '#33B1FF !important',
              color: colorScheme === 'dark' && '#fff',
            },
            '&:hover': {
              backgroundColor: colorScheme === 'dark' && '#2C2E33',
            },
          },
          month: {
            '& tr': {
              border: colorScheme === 'dark' && 'none !important',
              backgroundColor: colorScheme === 'dark' && '#25262B !important',
            },
          },
          monthPickerControlActive: {
            backgroundColor: '#33B1FF',
          },
          yearPickerControlActive: {
            backgroundColor: '#33B1FF',
          },
          calendarHeaderLevel: {
            color: colorScheme === 'dark' ? '#C1C2C5' : '#000',
            '&:hover': {
              backgroundColor: colorScheme === 'dark' && '#141517',
            },
          },
          calendarHeaderControl: {
            '&:hover': {
              backgroundColor: colorScheme === 'dark' && '#343a4033',
              color: colorScheme === 'dark' && '#E9ECEF',
            },
          },
          monthPickerControl: {
            color: colorScheme === 'dark' ? '#C1C2C5' : '',
            '&:hover': {
              backgroundColor: colorScheme === 'dark' && '#141517',
            },
          },
          yearPickerControl: {
            color: colorScheme === 'dark' ? '#C1C2C5' : '',
            '&:hover': {
              backgroundColor: colorScheme === 'dark' && '#141517',
            },
          },
          weekday: {
            color: colorScheme === 'dark' && '#A6A7AB',
          },
        }}
      />
    </div>
  );
}
